import React from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Badge } from "primereact/badge";
import { PackageItem } from "components/PackageItem";
import { IPackageItem } from "pages/Packages/View/api";
import { PACKAGE_ITEM_STATE_GROUPS } from "definitions";
import { PackageItemsEarnings } from "components/PackageItemsEarnings";

interface Props {
  packageItems: (IPackageItem & { accepted: boolean, rejected: boolean, accepting: boolean, rejecting: boolean })[];
  showEarnings?: boolean;
  onAcceptItem: (item: IPackageItem, price: number) => any;
  onRejectItem: (item: IPackageItem) => any;
  onItemPriceChanged: (item: IPackageItem, price: number) => any;
}

export const PackageItemsList = ({ packageItems, showEarnings, onAcceptItem, onRejectItem, onItemPriceChanged }: Props) => {
  const filterItemsForInput = (statuses: string[], extraConditionFn?: (item: IPackageItem) => boolean) => (
    packageItems.filter(item => statuses.includes(item.status) && (!extraConditionFn || extraConditionFn(item)))
  )
  const negotiable = filterItemsForInput(PACKAGE_ITEM_STATE_GROUPS.NEGOTIABLE);
  const unpacked = filterItemsForInput(PACKAGE_ITEM_STATE_GROUPS.PRE_OFFER);
  const approved = filterItemsForInput(PACKAGE_ITEM_STATE_GROUPS.PREPARING);
  const rejected = filterItemsForInput(PACKAGE_ITEM_STATE_GROUPS.REJECTED);
  const listed = filterItemsForInput(PACKAGE_ITEM_STATE_GROUPS.LISTED, item => item.soldQuantity === 0);
  const sold = filterItemsForInput(PACKAGE_ITEM_STATE_GROUPS.LISTED, item => item.soldQuantity !== 0);

  const initialActiveIndex = [negotiable, unpacked, approved, rejected, listed, sold].findIndex((items) => items.length > 0);

  if (packageItems && packageItems.length > 0) {
    return (
      <div className="col-12 not-printable">
         <Accordion activeIndex={initialActiveIndex}>
         <AccordionTab
            headerTemplate={
              <AccordionHeader title="Needs Your Attention" count={negotiable.length} />
            }
          >
            <PackageItemsOfStatusList
              items={negotiable}
              showPriceInformation
              onAcceptItem={onAcceptItem}
              onRejectItem={onRejectItem}
              onItemPriceChanged={onItemPriceChanged}
            />
          </AccordionTab>
          <AccordionTab
            headerTemplate={
              <AccordionHeader title="Pending Review" count={unpacked.length} />
            }
          >
            <PackageItemsOfStatusList items={unpacked} />
          </AccordionTab>
          <AccordionTab
            headerTemplate={
              <AccordionHeader title="Approved Items" count={approved.length} />
            }
          >
            <PackageItemsOfStatusList items={approved} showPriceInformation />
          </AccordionTab>
          <AccordionTab
            headerTemplate={
              <AccordionHeader title="Rejected Items" count={rejected.length} />
            }
          >
            <PackageItemsOfStatusList items={rejected} />
          </AccordionTab>
          <AccordionTab
            headerTemplate={
              <AccordionHeader title="Listed Items" count={listed.length} />
            }
          >
            <PackageItemsOfStatusList items={listed} showPriceInformation showSalesInformation />
          </AccordionTab>
          <AccordionTab
            headerTemplate={
              <AccordionHeader title="Sold Items" count={sold.length} />
            }
          >
            <PackageItemsOfStatusList items={sold} showPriceInformation showSalesInformation />
          </AccordionTab>
        </Accordion>
        {showEarnings ? (
          <div className="mt-3 mb-1">
            <PackageItemsEarnings packageItems={[...negotiable, ...approved, ...listed, ...sold]} />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="col-12">
      <p>We will add items to this package shortly.</p>
    </div>
  );
};

function AccordionHeader ({
  title,
  count,
}: {
  title: string;
  count: number;
}) {
  return (
    <div className="w-full flex align-items-center justify-content-between">
      <span className="p-accordion-header-text">{title}</span>
      <Badge value={count}></Badge>
    </div>
  );
};

function PackageItemsOfStatusList ({
  items,
  emptyMessage = "There are no items in this list",
  showPriceInformation,
  showSalesInformation,
  onAcceptItem,
  onRejectItem,
  onItemPriceChanged
}: {
  items: Props["packageItems"],
  emptyMessage?: string,
  showPriceInformation?: boolean,
  showSalesInformation?: boolean,
  onAcceptItem?: (item: IPackageItem, price: number) => any,
  onRejectItem?: (item: IPackageItem) => any,
  onItemPriceChanged?: (item: IPackageItem, price: number) => any;
}) {
  if (items.length === 0) return <p>{emptyMessage}</p>;
  return (
    <>
      {items.map((packageItem) => (
        <PackageItem
          packageItem={packageItem}
          key={packageItem.code}
          showPriceInformation={showPriceInformation}
          showSalesInformation={showSalesInformation}
          onAcceptItem={!!onAcceptItem ? (price) => onAcceptItem(packageItem, price) : undefined}
          onRejectItem={!!onRejectItem ? () => onRejectItem(packageItem) : undefined}
          onItemPriceChanged={!!onItemPriceChanged ? (price) => onItemPriceChanged(packageItem, price) : undefined}
        />
      ))}
    </>
  );
}
